import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useCosmopointsPrograms,
  useCreateCosmopointsConnection,
} from "@/api/hooks/use-query-api";
import { useToast } from "@/features/toast/hooks/use-toast";
import { ProgramLogo } from "./components/ProgramLogo";

interface LinkProgramDialogProps {
  open: boolean;
  onClose: () => void;
}

export const LinkProgramDialog: React.FC<LinkProgramDialogProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation();
  const { success, error: showError } = useToast();

  const {
    data: programsData,
    isLoading: isProgramsLoading,
    isError: isProgramsError,
  } = useCosmopointsPrograms(open);
  const { mutateAsync: createConnection, isPending } =
    useCreateCosmopointsConnection();

  const programs = useMemo(
    () => programsData?.data?.programs ?? [],
    [programsData]
  );

  const [programId, setProgramId] = useState<string | undefined>();
  const [account, setAccount] = useState("");

  useEffect(() => {
    if (!programId && programs.length > 0) setProgramId(programs[0].id);
  }, [programId, programs]);

  const selected = programs.find((p) => p.id === programId);
  // Programs advertise which identifier they need; membership number is the
  // default when they say nothing. The API normalises this to a name list,
  // but the vendor sends an object, so don't assume an array reached us.
  const required = Array.isArray(selected?.requiredFields)
    ? selected.requiredFields
    : Object.keys(selected?.requiredFields ?? {});
  const wantsEmail =
    required.includes("destinationUserEmail") &&
    !required.includes("destinationUserId");

  const handleClose = () => {
    setAccount("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!programId || !account.trim()) return;

    try {
      await createConnection({
        programId,
        destinationAccount: account.trim(),
        makeDefault: true,
      });
      success(
        t("settings.rewards.cosmopoints.linkSuccess", {
          program: selected?.name ?? programId,
        })
      );
      handleClose();
    } catch {
      // The API validates the membership with the partner program before
      // storing anything, so a failure here usually means a wrong number.
      showError(t("settings.rewards.cosmopoints.linkError"));
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={handleClose} />

      <div className="relative bg-white dark:bg-gray-800 rounded-[20px] shadow-xl max-w-md w-full mx-4 max-h-[80vh] md:max-h-[90vh] overflow-y-auto mt-16 md:mt-0">
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-zinc-800 dark:text-white">
              {t("settings.rewards.cosmopoints.linkTitle")}
            </h2>
            <button
              type="button"
              onClick={handleClose}
              className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
            >
              <X className="w-6 h-6 text-zinc-800 dark:text-white" />
            </button>
          </div>

          <p className="text-sm text-zinc-500 dark:text-gray-300">
            {t("settings.rewards.cosmopoints.linkDescription")}
          </p>

          {isProgramsLoading ? (
            <div className="h-24 bg-gray-50 dark:bg-gray-700 rounded-xl animate-pulse" />
          ) : isProgramsError || programs.length === 0 ? (
            <p className="text-sm text-destructive">
              {t("settings.rewards.cosmopoints.programsUnavailable")}
            </p>
          ) : (
            <>
              <div className="space-y-2">
                <label
                  htmlFor="link-program"
                  className="block text-sm font-semibold text-zinc-700 dark:text-gray-300"
                >
                  {t("settings.rewards.cosmopoints.programLabel")}
                </label>
                <Select value={programId} onValueChange={setProgramId}>
                  <SelectTrigger id="link-program">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {programs.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        <span className="flex items-center gap-2">
                          <ProgramLogo src={p.logo} className="h-5 w-5" />
                          <span className="truncate">{p.name}</span>
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label
                  htmlFor="link-account"
                  className="block text-sm font-semibold text-zinc-700 dark:text-gray-300"
                >
                  {wantsEmail
                    ? t("settings.rewards.cosmopoints.emailLabel")
                    : t("settings.rewards.cosmopoints.memberLabel")}
                </label>
                <Input
                  id="link-account"
                  type={wantsEmail ? "email" : "text"}
                  value={account}
                  onChange={(e) => setAccount(e.target.value)}
                  placeholder={
                    wantsEmail
                      ? t("settings.rewards.cosmopoints.emailPlaceholder")
                      : t("settings.rewards.cosmopoints.memberPlaceholder")
                  }
                />
                <p className="text-xs text-text-quaternary dark:text-gray-400">
                  {t("settings.rewards.cosmopoints.linkHint")}
                </p>
              </div>
            </>
          )}

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-3.5 py-2.5 rounded-lg bg-white dark:bg-gray-700 border border-border-primary dark:border-border-primary text-text-secondary dark:text-text-secondary text-sm font-semibold hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors shadow-sm"
            >
              {t("settings.rewards.cancel")}
            </button>
            <Button
              type="submit"
              variant="darkPrimary"
              disabled={!programId || !account.trim() || isPending}
              className="px-4 py-2.5 rounded-[10px] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isPending
                ? t("common.loading") + "..."
                : t("settings.rewards.cosmopoints.linkConfirm")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
